import React, { useState, useEffect } from 'react';
import { assessmentQuestions, progressMessages, educationalContent } from '../data/questions';
import { Question, AssessmentResult } from '../types/assessment';
import { AssessmentAPI } from '../utils/api';
import QuestionComponent from './QuestionComponent';
import ContactForm from './ContactForm';
import ResultsView from './ResultsView';

export const AssessmentFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [showInterstitial, setShowInterstitial] = useState<string | null>(null);
  const [autoAdvanceTimeout, setAutoAdvanceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessmentResult, setAssessmentResult] = useState<AssessmentResult | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const questions = assessmentQuestions;

  const getNextValidStep = (startIndex: number, currentResponses: Record<string, any>): number => {
    let nextStep = startIndex + 1;
    while (nextStep < questions.length) {
      const q = questions[nextStep];
      if (!q.condition || q.condition(currentResponses)) {
        return nextStep;
      }
      nextStep++;
    }
    return nextStep;
  };

  const getPreviousValidStep = (startIndex: number, currentResponses: Record<string, any>): number => {
    let prevStep = startIndex - 1;
    while (prevStep >= 0) {
      const q = questions[prevStep];
      if (!q.condition || q.condition(currentResponses)) {
        return prevStep;
      }
      prevStep--;
    }
    return -1;
  };

  const getInterstitialForStep = (step: number): string | null => {
    // Show interstitials after completing real questionnaire sections:
    // Section 1: q0-q10, Section 2: q11-q15, Section 3: q16-q18
    if (step === 10) return 'after_perma';
    if (step === 15) return 'after_sdt';
    if (step === 18) return 'after_mindset';
    return null;
  };

  const handleContactSubmit = async (contactInfo: any) => {
    if (!sessionId) {
      setError('No session available. Please restart the assessment.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Complete the assessment through the LLM backend
      const result = await AssessmentAPI.completeAssessment({
        session_id: sessionId,
        user_info: {
          email: contactInfo.email,
          name: contactInfo.name,
          phone: contactInfo.phone,
          gdpr_consent: contactInfo.gdpr_consent,
          marketing_consent: contactInfo.marketing_consent
        }
      });

      setAssessmentResult(result);
      setResultUrl(result.result_url || null);
      setIsCompleted(true);
      
    } catch (err) {
      setError('Failed to process assessment. Please try again.');
      console.error('Assessment completion error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactBack = () => {
    setShowContactForm(false);
  };
  
  const currentQuestion = questions[currentStep];
  const isLastQuestion = getNextValidStep(currentStep, responses) >= questions.length;
  
  // Handle dynamic options
  const effectiveQuestion = currentQuestion && currentQuestion.dynamicOptions 
    ? { ...currentQuestion, options: currentQuestion.dynamicOptions(responses) }
    : currentQuestion;

  const getVisibleQuestionsCount = (currentResponses: Record<string, any>): number => {
    let count = 0;
    for (const q of questions) {
      if (!q.condition) {
        count++;
      } else {
        // Only count conditional questions if their condition is met
        try {
          if (q.condition(currentResponses)) {
            count++;
          }
        } catch (e) {
          // If condition fails (e.g. data not yet available), don't count it
        }
      }
    }
    return count;
  };

  const getCurrentQuestionNumber = (currentResponses: Record<string, any>, currentIdx: number): number => {
    let count = 0;
    for (let i = 0; i <= currentIdx; i++) {
      const q = questions[i];
      // Only count the current question if it is actually visible/active
      if (!q.condition) {
        count++;
      } else {
        try {
          if (q.condition(currentResponses)) {
            count++;
          }
        } catch (e) {}
      }
    }
    return count;
  };

  const visibleQuestionsCount = getVisibleQuestionsCount(responses);
  const currentQuestionNumber = getCurrentQuestionNumber(responses, currentStep);
  const progress = Math.round((currentQuestionNumber / visibleQuestionsCount) * 100);

  const getMiniInsight = (responses: Record<string, any>) => {
    // Logic from MD: Q7, Q8, Q10 + Calculation from Q11-13 & Q15-18
    let priority = responses['q7_priority_area'];
    if (!priority) {
      const areas = responses['q5_life_areas'] || [];
      priority = areas.length === 1 ? areas[0] : 'Nezadáno';
    }
    const pressure = responses['q8_pressure_source'] || 'Nezadáno';
    const brake = responses['q10_action_block'] || 'Nezadáno';
    
    // Simple logic for recommended style based on Q17 (Support preference)
    // In a real app, this might be more complex
    const supportStyle = responses['q17_support_preference'] || 'Doporučíme na míru';

    return {
      priority,
      pressure,
      brake,
      supportStyle
    };
  };

  const MiniInsight: React.FC<{ 
    responses: Record<string, any>, 
    onContinue: () => void 
  }> = ({ responses, onContinue }) => {
    const insight = getMiniInsight(responses);
    
    // Helper to format values for display
    const formatValue = (questionId: string, val: string) => {
        if (questionId === 'q17_support_preference') {
            const mappings: Record<string, string> = {
                'self_help': 'Samostatný postup (self-help)',
                'group': 'Jemné vedení a komunita (skupina)',
                'individual': '1:1 podpora (kouč / mentor / terapeut)',
                'recommend': 'Doporučení na míru'
            };
            if (mappings[val]) return mappings[val];
        }
        
        const q = questions.find(q => q.id === questionId);
        if (!q) return val;
        
        let opts = q.options || [];
        if (q.dynamicOptions) {
            try {
                opts = q.dynamicOptions(responses) || opts;
            } catch (e) {
                // Ignore error if dynamic options fail
            }
        }
        
        const opt = opts.find((o: any) => o.value === val);
        return opt ? opt.label : val;
    };

    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '3rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#667eea', marginBottom: '1.5rem', fontSize: '1.8rem' }}>
            Rychlý insight z tvých odpovědí
          </h3>
          
          <div style={{ textAlign: 'left', marginBottom: '2rem', fontSize: '1.1rem', lineHeight: '1.8' }}>
            <p><strong>Priorita #1:</strong> {formatValue('q7_priority_area', insight.priority)}</p>
            <p><strong>Nejčastější zdroj tlaku:</strong> {formatValue('q8_pressure_source', insight.pressure)}</p>
            <p><strong>Největší brzda:</strong> {formatValue('q10_action_block', insight.brake)}</p>
            <p><strong>Preferovaný styl podpory (náhled):</strong> {formatValue('q17_support_preference', insight.supportStyle)}</p>
          </div>

          <p style={{ color: '#666', marginBottom: '2rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
            Výsledek není obecná rada. Plán rozvoje ti připravíme na míru.
          </p>

          <button
            onClick={onContinue}
            style={{
              padding: '1rem 2.5rem',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#667eea',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            Zobrazit kompletní vyhodnocení
          </button>
        </div>
      </div>
    );
  };

  // Initialize session on component mount
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setIsLoading(true);
        const sessionData = await AssessmentAPI.startAssessment();
        setSessionId(sessionData.session_id);
        setError(null);
      } catch (err) {
        setError('Failed to start assessment session. Please try again.');
        console.error('Session initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, []);

  const handleAnswer = async (value: any) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));

    // Submit response to backend if we have a session
    if (sessionId) {
      try {
        await AssessmentAPI.submitResponse({
          session_id: sessionId,
          question_id: currentQuestion.id,
          answer_value: value
        });
      } catch (err) {
        console.error('Failed to submit response:', err);
        // Don't show error to user, allow offline usage
      }
    }

    // Auto-advance logic
    const shouldAutoAdvance = shouldAutoAdvanceForQuestion(currentQuestion, value);
    if (shouldAutoAdvance && !isLastQuestion) {
      // Clear any existing timeout
      if (autoAdvanceTimeout) {
        clearTimeout(autoAdvanceTimeout);
      }
      
      // Set a small delay for better UX
      const timeout = setTimeout(() => {
        handleNext();
      }, 800);
      
      setAutoAdvanceTimeout(timeout);
    }
  };

  const shouldAutoAdvanceForQuestion = (question: Question, value: any): boolean => {
    switch (question.type) {
      case 'scale':
        return false; // Manual advance for scale questions
      case 'single_choice':
        return true; // Always auto-advance for single choice
      case 'multiple_choice':
        // Auto-advance if max selections reached
        const selections = Array.isArray(value) ? value : [];
        return question.validation.max_selections ? selections.length >= question.validation.max_selections : false;
      case 'text':
        return false; // Never auto-advance for text input
      default:
        return false;
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeout) {
        clearTimeout(autoAdvanceTimeout);
      }
    };
  }, [autoAdvanceTimeout]);

  const [showMiniInsight, setShowMiniInsight] = useState(false);

  const handleNext = () => {
    // Clear any pending auto-advance
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    // Check if we should show an interstitial
    const interstitial = getInterstitialForStep(currentStep);
    if (interstitial && !showInterstitial) {
      setShowInterstitial(interstitial);
      return;
    }

    const nextStep = getNextValidStep(currentStep, responses);

    if (nextStep >= questions.length) {
      // Show Mini Insight BEFORE Contact Form
      setShowMiniInsight(true);
    } else {
      setCurrentStep(nextStep);
      setShowInterstitial(null);
    }
  };

  const handleMiniInsightContinue = () => {
    setShowMiniInsight(false);
    setShowContactForm(true);
  };

  const handlePrevious = () => {
    const prevStep = getPreviousValidStep(currentStep, responses);
    if (prevStep >= 0) {
      setCurrentStep(prevStep);
    }
  };

  // Show interstitial content
  if (showInterstitial) {
    const content = educationalContent[showInterstitial as keyof typeof educationalContent];
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '3rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#667eea', marginBottom: '1rem', fontSize: '1.5rem' }}>
            {content.title}
          </h3>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '2rem' }}>
            {content.content}
          </p>
          <button
            onClick={handleNext}
            style={{
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#667eea',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            Pokračovat →
          </button>
        </div>
      </div>
    );
  }

  // Show completion message with results
  if (isCompleted) {
    return (
      <ResultsView
        assessmentResult={assessmentResult}
        resultUrl={resultUrl}
        showFollowUp={true}
      />
    );
  }

  // Show Mini Insight
  if (showMiniInsight) {
    return <MiniInsight responses={responses} onContinue={handleMiniInsightContinue} />;
  }

  // Show contact form
  if (showContactForm) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
        {isLoading ? (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '3rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#667eea', marginBottom: '1rem' }}>Vyhodnocujeme tvoje odpovědi...</h3>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Sestavujeme deterministické vyhodnocení a vybíráme doporučení, která sedí na tvoje téma, kapacitu a preferovanou podporu.
            </p>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        ) : (
          <ContactForm 
            onSubmit={handleContactSubmit}
            onBack={handleContactBack}
          />
        )}
      </div>
    );
  }

  // Show loading state
  if (isLoading && !currentQuestion) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '3rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ color: '#667eea', marginBottom: '1rem' }}>Připravujeme váš assessment...</h3>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '3rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Chyba</h3>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#667eea',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
      {/* Progress */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          width: '100%', 
          height: '8px', 
          backgroundColor: '#e0e0e0', 
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#667eea',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
          <p style={{ color: '#666', margin: 0 }}>
            Otázka {currentQuestionNumber} z {visibleQuestionsCount}
          </p>
          <p style={{ color: '#667eea', margin: 0, fontWeight: 'bold' }}>
            {progress}%
          </p>
        </div>
        
        {/* Progress message */}
        {progressMessages[progress as keyof typeof progressMessages] && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#f0f4ff', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#667eea', margin: 0, fontWeight: '500' }}>
              {progressMessages[progress as keyof typeof progressMessages]}
            </p>
          </div>
        )}
      </div>

      {/* Question Component */}
      <QuestionComponent
        question={effectiveQuestion}
        value={responses[currentQuestion.id]}
        onChange={handleAnswer}
        onNext={handleNext}
        onPrevious={handlePrevious}
        canGoBack={currentStep > 0}
        isLast={isLastQuestion}
      />

    </div>
  );
};